import { Suspense, useState } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { TTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { RocketSVG } from '@svgComponents/rocketSVG/RocketSVG';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useGetTopupApprovedBannerData } from './useGetTopupApprovedBannerData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { useNewTopUpDesignSplitTest } from '../../../topupRequest/TopUpChoiceForm/useNewTopUpDesignSplitTest';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import { DrawdownActionPanel } from '../drawdownActionPanel/DrawdownActionPanel';

export const TopupApprovedBanner = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const [drawdownPanelIsOpen, setDrawdownPanelIsOpen] = useState(false);
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div data-testId="topup-approved-banner-loading">
                        <MainContentPending />
                    </div>
                }
            >
                <BannerContent
                    accountId={accountId}
                    setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                />
                {drawdownPanelIsOpen && (
                    <div className="col-span-5 mt-[-36px]">
                        <DrawdownActionPanel
                            accountId={accountId}
                            setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                        />
                    </div>
                )}
            </Suspense>
        </ErrorBoundary>
    );
};

const BannerContent = ({
    accountId,
    setDrawdownPanelIsOpen
}: {
    accountId: NonNullable<TAccount['account_id']>;
    setDrawdownPanelIsOpen: any;
}) => {
    const {
        topUpApprovalTimeOut,
        availableCredit,
        topupStatus,
        isRBL,
        isRepaymentReduction
    } = useGetTopupApprovedBannerData(accountId);
    const { branch } = useNewTopUpDesignSplitTest();
    const history = useHistory();

    const redirectToApprovalPage = () => {
        history.push(
            maintainStateKey({
                pathname: `/approved/${accountId}/`
            })
        );
    };

    return (
        <>
            <div className="col-span-5 grid grid-flow-dense grid-cols-7 gap-m rounded-s border border-success-mid bg-success-lt p-l max-sm:col-span-full">
                <div className="col-span-1 m-auto max-sm:col-span-full">
                    <RocketSVG className="h-4xl w-4xl text-success-drk" />
                </div>
                <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                    <Text className="text-xl font-bold">
                        <BannerTitle
                            topupStatus={topupStatus}
                            isRepaymentReduction={isRepaymentReduction}
                        />
                    </Text>
                    <Text className="text-l">
                        {isRepaymentReduction ? (
                            <FormattedMessage
                                id="_.topup_approved_banner.repayment_reudction_description"
                                defaultMessage="You can adjust your loan terms and reduce your mimimum monthly repayments."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.topup_approved_banner.topup_approved_description"
                                defaultMessage="You've got another {amount} to take before {expiryDate}"
                                values={{
                                    amount: (
                                        <MoneyWithDecimals
                                            value={availableCredit}
                                        />
                                    ),
                                    expiryDate: (
                                        <FormattedDate
                                            value={
                                                new Date(topUpApprovalTimeOut!)
                                            }
                                            day="2-digit"
                                            month="short"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        )}
                    </Text>
                </div>
                <div className="col-span-4 max-sm:col-span-full max-sm:text-center min-m:col-start-2">
                    <Button
                        onClick={() => {
                            tracking.ampli.topupApprovedBannerCtaClicked({
                                account_id: accountId
                            });
                            if (branch === 'INCLUDE' && !isRBL) {
                                redirectToApprovalPage();
                            } else {
                                setDrawdownPanelIsOpen(true);
                            }
                        }}
                    >
                        {isRepaymentReduction ? (
                            <FormattedMessage
                                id="_.topup_approved_banner.adjust_terms.cta"
                                defaultMessage="Adjust your terms"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.topup_approved_banner.choose_drawdown_amount.cta"
                                defaultMessage="Choose how much"
                            />
                        )}
                    </Button>
                </div>
            </div>
        </>
    );
};

const BannerTitle = ({
    topupStatus,
    isRepaymentReduction
}: {
    topupStatus: TTopUpStatus;
    isRepaymentReduction: boolean;
}) => {
    if (isRepaymentReduction)
        return (
            <FormattedMessage
                id="_.topup_approved_banner.repayment_reduction_approved"
                defaultMessage="You can now reduce your minimum monthly repayments"
            />
        );

    if (topupStatus === 'PRE_APPROVED')
        return (
            <FormattedMessage
                id="_.topup_approved_banner.preapproved_title"
                defaultMessage="You've been automatically approved!"
            />
        );

    return (
        <FormattedMessage
            id="_.topup_approved_banner.topup_approved_title"
            defaultMessage="We've approved your business for a top up!"
        />
    );
};
